'use client';

/**
 * @fileoverview `DiscoverPickedForYouHeroConnected` — client wrapper that
 * renders the DS "Picked for you" pick set atop the Discover list (WEB-677 /
 * DPL-27, superseding the single-event FRE-915 hero) ONLY when the
 * authenticated viewer has a real taste signal and the response carries a
 * `pickedForYou` pointer that resolves on the page.
 *
 * File kept in place (not renamed) — WEB-677's "Files affected" list pins
 * this exact path for the "supply up to three picks" change.
 *
 * The presentational {@link DiscoverPickSet} stays pure. This wrapper reads
 * `useAuth()` + the SHARED `useDiscoverEventsQuery(buildDiscoverQueryArgs(
 * ...))` subscription — the SAME cache entry the list view / results bar
 * already subscribe to — so no second `/events/discover` request fires (the
 * cold-load single-fetch invariant is preserved). The gating + honest
 * derivation live in {@link buildDiscoverPickSet}; when it returns `null`
 * this renders nothing and the list is unchanged (anonymous / soaking / no
 * pick).
 *
 * @since FRE-915 (original single-pick hero)
 * @since WEB-677 (Discovery v2 — DPL-27) — three-up pick set
 */
import * as React from 'react';

import type { CityCode } from '@loci/city-editions';
import { useDiscoverEventsQuery } from '@loci/api';

import { DiscoverPickSet } from '@/components/molecules/DiscoverPickSet';
import { useAuth } from '@/contexts/AuthContext';
import { useDiscoverParams } from '@/hooks/useDiscoverParams';
import { useCitySurface } from '@/lib/city-context.client';
import { useDiscoverEventArgs } from '@/lib/discover/useDiscoverEventArgs';
import { buildDiscoverPickSet } from '@/lib/discover/pickSet';

export interface DiscoverPickedForYouHeroConnectedProps {
  /** Resolved city code — drives the shared subscription cache key + detail hrefs. */
  readonly cityCode: CityCode;
  /** Additional CSS classes forwarded to the pick-set section. */
  readonly className?: string;
}

export function DiscoverPickedForYouHeroConnected({
  cityCode,
  className,
}: DiscoverPickedForYouHeroConnectedProps): React.ReactElement | null {
  const { isAuthenticated } = useAuth();
  const { params } = useDiscoverParams();
  const surface = useCitySurface();

  const queryArgs = useDiscoverEventArgs(params, cityCode);
  // Shared subscription — same cache key as the list view / results bar, so no
  // extra network request fires (cold-load single-fetch invariant preserved).
  const { data } = useDiscoverEventsQuery(queryArgs);

  const pickSet = React.useMemo(
    () =>
      buildDiscoverPickSet(
        {
          isAuthenticated,
          envelope: data?.authenticatedEnvelope,
          personalized: data?.meta?.personalized,
          total: data?.meta?.total,
          tasteMatchedTotal: data?.meta?.tasteMatchedTotal,
          cityCode,
          surface,
        },
        data?.events,
      ),
    [isAuthenticated, data, cityCode, surface],
  );

  if (!pickSet) return null;

  return (
    <DiscoverPickSet
      eyebrow={pickSet.eyebrow}
      caption={pickSet.caption}
      picks={pickSet.picks}
      className={className}
    />
  );
}
